/** QuickPlan — Illustrations de notice (rendu three.js hors écran, style « notice IKEA »).
 *
 * Fond blanc, arêtes au trait noir (lignes épaisses en pixels), pièces déjà montées
 * en gris clair, pièces de l'étape en couleur pâle. Les fixations de l'étape sont
 * sorties de leur trou le long de leur axe, reliées par un pointillé ; les pièces
 * peuvent aussi être éclatées. Rend une image PNG et la position des repères (lettres).
 */
import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { geometryFor, edgesFor, partKey, fixingGeometry } from '../viewer3d/cache.js';
import { resolvedOf } from '../geom.js';
import { computeFrames } from '../../shared/frames.js';

const DEG = Math.PI / 180;

/** Directions de vue (vers la caméra), Z en haut. */
import { NOTICE_VIEWS } from './views.js';

export { NOTICE_VIEWS, OPPOSITE_VIEW } from './views.js';

let renderer = null;
function getRenderer() {
  if (!renderer) {
    renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, alpha: false });
    renderer.setPixelRatio(1);
    renderer.setClearColor('#ffffff', 1);
  }
  return renderer;
}

// Géométries de traits mémorisées (par primitive, et par type de fixation).
const lineGeos = new Map();
function lineGeometryFor(key, edges) {
  let g = lineGeos.get(key);
  if (!g) {
    if (lineGeos.size > 2000) { for (const v of lineGeos.values()) v.dispose(); lineGeos.clear(); }
    g = new LineSegmentsGeometry().fromEdgesGeometry(edges);
    lineGeos.set(key, g);
  }
  return g;
}

const fillMats = new Map();
function fillMaterial(color) {
  let m = fillMats.get(color);
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color: new THREE.Color(color), polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 });
    fillMats.set(color, m);
  }
  return m;
}

/** Couleur de pièce éclaircie vers le blanc (lisible imprimée en noir et blanc). */
function pale(hex, k = 0.55) {
  const c = new THREE.Color(hex);
  return `#${c.lerp(new THREE.Color('#ffffff'), k).getHexString()}`;
}

function frameMatrix(f) {
  return new THREE.Matrix4().set(
    f.R[0][0], f.R[0][1], f.R[0][2], f.t[0],
    f.R[1][0], f.R[1][1], f.R[1][2], f.t[1],
    f.R[2][0], f.R[2][1], f.R[2][2], f.t[2],
    0, 0, 0, 1,
  );
}

let framesMemo = { project: null, lookup: null, value: null };
/** Repères monde, articulations fermées (même convention que computeNotice). */
function closedFrames(project, lookup) {
  if (framesMemo.project !== project || framesMemo.lookup !== lookup) {
    const closed = { ...project, instances: project.instances.map((i) => ({ ...i, open: 0 })) };
    framesMemo = { project, lookup, value: computeFrames(closed, (i) => resolvedOf(i, lookup)) };
  }
  return framesMemo.value;
}

/**
 * Rend une illustration.
 *  ids        : instances montrées
 *  newIds     : celles de l'étape (en couleur, repérées) — les autres en gris
 *  fixings    : fixations monde (notice.fixings) des instances de l'étape, à éclater
 *  letters    : instId → lettre de repère
 *  solo       : rendre une seule instance dans son propre repère (vignette d'inventaire)
 */
export function renderIllustration(project, lookup, opts) {
  const { ids, newIds = new Set(), fixings = [], letters = {}, view = 'av-d', explode = false, width = 1400, height = 950, solo = false, allNew = false, labelAll = false } = opts;
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight('#ffffff', '#b9bec6', 1.9));
  const sun = new THREE.DirectionalLight('#ffffff', 1.1);
  const vdir = new THREE.Vector3(...(NOTICE_VIEWS[view] ?? NOTICE_VIEWS['av-d']).dir).normalize();
  sun.position.copy(vdir.clone().add(new THREE.Vector3(0.6, 0.2, 1.2)).multiplyScalar(10000));
  scene.add(sun);

  const F = closedFrames(project, lookup);
  const byId = new Map(project.instances.map((i) => [i.id, i]));
  const resolution = new THREE.Vector2(width, height);
  const lineMat = (color, lw, dashed = false, dash = 10) => {
    const m = new LineMaterial({ color: new THREE.Color(color), linewidth: lw, dashed, dashSize: dash, gapSize: dash * 0.7, worldUnits: false });
    m.resolution.copy(resolution);
    return m;
  };
  const mats = { edgeNew: lineMat('#111418', 2.4), edgePrev: lineMat('#9ba2aa', 1.3), edgeFix: lineMat('#111418', 1.4) };
  const disposables = Object.values(mats);
  const points = []; // pour le cadrage
  const labelAnchors = [];

  // ── Éclaté des pièces : chaque « racine » nouvelle s'écarte de l'ensemble déjà monté ──
  const offsets = new Map();
  const extent = { lo: [Infinity, Infinity, Infinity], hi: [-Infinity, -Infinity, -Infinity] };
  const boxOf = (inst) => {
    const r = resolvedOf(inst, lookup);
    const f = solo ? null : F.get(inst.id);
    if (!r || (!f && !solo)) return null;
    const m = solo ? new THREE.Matrix4() : frameMatrix(f);
    return new THREE.Box3(new THREE.Vector3(...r.bbox.min), new THREE.Vector3(...r.bbox.max)).applyMatrix4(m);
  };
  const prevBox = new THREE.Box3();
  for (const id of ids) {
    const inst = byId.get(id);
    const b = inst && boxOf(inst);
    if (!b) continue;
    for (let k = 0; k < 3; k++) { extent.lo[k] = Math.min(extent.lo[k], b.min.getComponent(k)); extent.hi[k] = Math.max(extent.hi[k], b.max.getComponent(k)); }
    if (!newIds.has(id)) prevBox.union(b);
  }
  const span = Math.max(100, ...[0, 1, 2].map((k) => extent.hi[k] - extent.lo[k]).filter(Number.isFinite));
  if (explode && !solo && !prevBox.isEmpty()) {
    const pc = prevBox.getCenter(new THREE.Vector3());
    const half = prevBox.getSize(new THREE.Vector3()).multiplyScalar(0.5);
    for (const id of newIds) {
      const inst = byId.get(id);
      if (!inst || (inst.parentId && newIds.has(inst.parentId))) continue; // les enfants suivent leur racine
      const b = boxOf(inst);
      if (!b) continue;
      const c = b.getCenter(new THREE.Vector3());
      let best = 2;
      let score = -Infinity;
      for (let k = 0; k < 3; k++) {
        const s = Math.abs(c.getComponent(k) - pc.getComponent(k)) / Math.max(1, half.getComponent(k));
        if (s > score) { score = s; best = k; }
      }
      const off = new THREE.Vector3();
      off.setComponent(best, Math.sign(c.getComponent(best) - pc.getComponent(best) || 1) * span * 0.32);
      offsets.set(id, off);
    }
    // Propagation aux descendants nouveaux.
    let grew = true;
    while (grew) {
      grew = false;
      for (const id of newIds) {
        const inst = byId.get(id);
        if (!offsets.has(id) && inst?.parentId && offsets.has(inst.parentId)) { offsets.set(id, offsets.get(inst.parentId)); grew = true; }
      }
    }
  }

  const guides = []; // pointillés [a, b]
  for (const id of ids) {
    const inst = byId.get(id);
    if (!inst) continue;
    const comp = lookup(inst.componentId);
    const res = resolvedOf(inst, lookup);
    const f = solo ? null : F.get(id);
    if (!comp || !res || (!f && !solo)) continue;
    const isNew = allNew || newIds.has(id);
    const base = solo ? new THREE.Matrix4() : frameMatrix(f);
    const off = offsets.get(id);
    if (off) {
      base.premultiply(new THREE.Matrix4().makeTranslation(off.x, off.y, off.z));
      const b = boxOf(inst);
      if (b) {
        const c = b.getCenter(new THREE.Vector3());
        guides.push([c.clone(), c.clone().add(off)]);
      }
    }
    for (const p of res.parts) {
      const m = base.clone().multiply(new THREE.Matrix4().compose(
        new THREE.Vector3(...p.pos),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(...(p.rot ?? [0, 0, 0]).map((d) => d * DEG), 'XYZ')),
        new THREE.Vector3(1, 1, 1),
      ));
      const color = isNew ? pale(inst.color ?? p.color ?? comp.color) : '#eef0f2';
      const mesh = new THREE.Mesh(geometryFor(p), fillMaterial(color));
      mesh.matrixAutoUpdate = false;
      mesh.matrix.copy(m);
      mesh.userData.instId = id;
      scene.add(mesh);
      const lines = new LineSegments2(lineGeometryFor(partKey(p), edgesFor(p)), isNew ? mats.edgeNew : mats.edgePrev);
      lines.matrixAutoUpdate = false;
      lines.matrix.copy(m);
      scene.add(lines);
      if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
      const bb = mesh.geometry.boundingBox;
      for (const x of [bb.min.x, bb.max.x]) for (const y of [bb.min.y, bb.max.y]) for (const z of [bb.min.z, bb.max.z]) points.push(new THREE.Vector3(x, y, z).applyMatrix4(m));
    }
    if (isNew && letters[id] && !solo) {
      const b = new THREE.Box3(new THREE.Vector3(...res.bbox.min), new THREE.Vector3(...res.bbox.max)).applyMatrix4(base);
      labelAnchors.push({ id, letter: letters[id], p: b.getCenter(new THREE.Vector3()), box: b });
    }
  }

  // ── Fixations de l'étape, sorties de leur trou ──
  for (const fx of fixings) {
    if (fx.posable || fx.type === 'percage') continue; // instances déjà dessinées / trou seul
    const dir = new THREE.Vector3(...fx.dir).normalize();
    const pull = Math.max(15, fx.l * 0.9 + 12);
    const off = offsets.get(fx.instId) ?? new THREE.Vector3();
    const head = new THREE.Vector3(...fx.pos).add(off);
    const at = head.clone().addScaledVector(dir, pull);
    const geo = fixingGeometry(fx);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir);
    const m = new THREE.Matrix4().compose(at, q, new THREE.Vector3(1, 1, 1));
    const mesh = new THREE.Mesh(geo, fillMaterial('#7d838b'));
    mesh.matrixAutoUpdate = false;
    mesh.matrix.copy(m);
    scene.add(mesh);
    const key = `fixedge:${fx.type}:${Math.max(2.5, fx.d)}:${fx.l}`;
    let eg = lineGeos.get(key);
    if (!eg) { eg = new LineSegmentsGeometry().fromEdgesGeometry(new THREE.EdgesGeometry(geo, 40)); lineGeos.set(key, eg); }
    const el = new LineSegments2(eg, mats.edgeFix);
    el.matrixAutoUpdate = false;
    el.matrix.copy(m);
    scene.add(el);
    guides.push([at.clone().addScaledVector(dir, -fx.l), head.clone().addScaledVector(dir, -fx.l)]);
    points.push(at.clone().addScaledVector(dir, Math.max(2.5, fx.d)), head);
  }

  if (guides.length) {
    const pos = [];
    for (const [a, b] of guides) pos.push(a.x, a.y, a.z, b.x, b.y, b.z);
    const g = new LineSegmentsGeometry().setPositions(pos);
    const mat = lineMat('#3b4149', 1.3, true, span / 140);
    const l = new LineSegments2(g, mat);
    l.computeLineDistances();
    scene.add(l);
    disposables.push(g, mat);
  }

  // ── Caméra orthographique cadrée sur tout ce qui est dessiné ──
  if (!points.length) points.push(new THREE.Vector3(0, 0, 0));
  const center = new THREE.Box3().setFromPoints(points).getCenter(new THREE.Vector3());
  const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, span * 40 + 100000);
  cam.up.set(0, 0, 1);
  cam.position.copy(center).addScaledVector(vdir, span * 10 + 20000);
  cam.lookAt(center);
  cam.updateMatrixWorld();
  let x0 = Infinity; let x1 = -Infinity; let y0 = Infinity; let y1 = -Infinity;
  for (const p of points) {
    const v = p.clone().applyMatrix4(cam.matrixWorldInverse);
    x0 = Math.min(x0, v.x); x1 = Math.max(x1, v.x); y0 = Math.min(y0, v.y); y1 = Math.max(y1, v.y);
  }
  const margin = solo ? 0.06 : 0.08;
  let w = (x1 - x0) * (1 + 2 * margin) || 100;
  let h = (y1 - y0) * (1 + 2 * margin) || 100;
  const aspect = width / height;
  if (w / h > aspect) h = w / aspect; else w = h * aspect;
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  Object.assign(cam, { left: cx - w / 2, right: cx + w / 2, top: cy + h / 2, bottom: cy - h / 2 });
  cam.updateProjectionMatrix();

  const r = getRenderer();
  r.setSize(width, height, false);
  r.render(scene, cam);
  const url = r.domElement.toDataURL('image/png');

  // Repères : une étiquette par pièce nouvelle (une par lettre si l'étape est chargée).
  // Vue d'ensemble (labelAll) : une par pièce, posée sur une partie VISIBLE de la pièce
  // (lancer de rayon depuis la caméra) ; les pièces entièrement cachées sont rendues à part.
  const many = !labelAll && labelAnchors.length > 10;
  const seen = new Set();
  const labels = [];
  const hidden = [];
  const meshes = labelAll ? scene.children.filter((o) => o.isMesh && o.userData.instId) : [];
  if (labelAll) scene.updateMatrixWorld(true);
  const ray = new THREE.Raycaster();
  const FR = [0.5, 0.3, 0.7, 0.15, 0.85];
  const visiblePoint = (a) => {
    const { min, max } = a.box;
    const cand = [];
    for (const fx of FR) for (const fy of FR) for (const fz of FR) {
      cand.push(new THREE.Vector3(min.x + (max.x - min.x) * fx, min.y + (max.y - min.y) * fy, min.z + (max.z - min.z) * fz));
    }
    for (const c of cand) {
      const ndc = c.clone().project(cam);
      if (Math.abs(ndc.x) > 0.96 || Math.abs(ndc.y) > 0.94) continue;
      ray.setFromCamera(new THREE.Vector2(ndc.x, ndc.y), cam);
      const hit = ray.intersectObjects(meshes, false)[0];
      if (hit?.object.userData.instId === a.id) return ndc;
    }
    return null;
  };
  for (const a of labelAnchors) {
    if (many && seen.has(a.letter)) continue;
    seen.add(a.letter);
    const v = labelAll ? visiblePoint(a) : a.p.clone().project(cam);
    if (!v) { hidden.push(a.letter); continue; }
    labels.push({ x: (v.x + 1) / 2, y: (1 - v.y) / 2, text: a.letter });
  }

  // Étiquettes trop proches : on les écarte (quelques passes de répulsion, en pixels de
  // l'image). Distance mini ≈ diamètre d'un rond une fois l'image mise en page (~6 %).
  const MIN = width * 0.06;
  for (let pass = 0; pass < 30; pass++) {
    let moved = false;
    for (let i = 0; i < labels.length; i++) {
      for (let j = i + 1; j < labels.length; j++) {
        const a = labels[i];
        const b = labels[j];
        let dx = (b.x - a.x) * width;
        let dy = (b.y - a.y) * height;
        const d = Math.hypot(dx, dy);
        if (d >= MIN) continue;
        if (d < 0.01) { dx = 1; dy = 0; }
        const push = (MIN - d) / 2 / (Math.hypot(dx, dy) || 1);
        a.x -= (dx * push) / width; a.y -= (dy * push) / height;
        b.x += (dx * push) / width; b.y += (dy * push) / height;
        moved = true;
      }
    }
    if (!moved) break;
  }

  for (const d of disposables) d.dispose();
  return { url, width, height, labels, hidden: [...new Set(hidden)] };
}
